import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { AbigailBackdrop } from "@/components/abigail/AbigailBackdrop";
import { PlatformHeader } from "@/components/abigail/PlatformHeader";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";
import { fetchMe, meQueryKey, type Me } from "@/lib/auth";

export const Route = createFileRoute("/_authenticated/admin/diagnosticos")({
  beforeLoad: async ({ context }) => {
    const cached = context.queryClient.getQueryData<Me | null>(meQueryKey);
    const me =
      cached ??
      (await context.queryClient.fetchQuery({ queryKey: meQueryKey, queryFn: fetchMe }));
    if (!me?.admin) throw redirect({ to: "/chat" });
  },
  component: AdminDiagnosticosPage,
});

type DiagnosticoItem = {
  userId: string;
  email: string;
  fullName: string | null;
  conversationId: string;
  concluidoEm: string;
};

type DiagnosticoDetalhe = {
  userId: string;
  email: string;
  fullName: string | null;
  concluidoEm: string;
  diagnostico: string;
};

const DIAGNOSTICOS_KEY = ["admin", "diagnosticos"] as const;

function formatDate(value: string | null) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
}

function AdminDiagnosticosPage() {
  const [selecionado, setSelecionado] = useState<DiagnosticoItem | null>(null);

  const { data: diagnosticos = [], isLoading } = useQuery({
    queryKey: DIAGNOSTICOS_KEY,
    queryFn: () => api.get<DiagnosticoItem[]>("/api/admin/diagnosticos"),
  });

  return (
    <div className="relative flex min-h-dvh flex-col">
      <AbigailBackdrop dim />
      <PlatformHeader />

      <main className="flex flex-1 flex-col px-4 py-6 sm:px-6">
        <div className="mx-auto w-full max-w-6xl">
          <h1 className="font-serif text-ember text-3xl">Diagnósticos</h1>

          <div className="mt-6 overflow-hidden rounded-xl border border-border bg-card/80 shadow-card-deep backdrop-blur-md">
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Concluído em</TableHead>
                      <TableHead className="w-40"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {diagnosticos.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="py-10 text-center text-muted-foreground">
                          Nenhum diagnóstico concluído ainda.
                        </TableCell>
                      </TableRow>
                    ) : (
                      diagnosticos.map((d) => (
                        <TableRow key={d.userId}>
                          <TableCell className="font-medium">{d.fullName ?? "—"}</TableCell>
                          <TableCell className="text-muted-foreground">{d.email}</TableCell>
                          <TableCell>{formatDate(d.concluidoEm)}</TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-2"
                              onClick={() => setSelecionado(d)}
                            >
                              <FileText className="h-4 w-4" />
                              Ver diagnóstico
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </main>

      <DiagnosticoDialog
        item={selecionado}
        onClose={() => setSelecionado(null)}
      />
    </div>
  );
}

function DiagnosticoDialog({
  item,
  onClose,
}: {
  item: DiagnosticoItem | null;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: [...DIAGNOSTICOS_KEY, item?.userId],
    queryFn: () => api.get<DiagnosticoDetalhe>(`/api/admin/diagnosticos/${item!.userId}`),
    enabled: Boolean(item),
  });

  return (
    <Dialog open={Boolean(item)} onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent className="flex max-h-[85vh] flex-col border-border bg-card sm:max-w-2xl">
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-serif text-ember text-xl">
            {item?.fullName ?? item?.email ?? "Diagnóstico"}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">{item?.email}</p>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : isError ? (
            <p className="py-10 text-center text-sm text-destructive">
              Não foi possível carregar este diagnóstico.
            </p>
          ) : (
            <div className="prose-abigail max-w-none text-sm leading-relaxed text-foreground [&_a]:text-accent [&_code]:text-accent [&_h1]:font-serif [&_h2]:mt-5 [&_h2]:font-serif [&_h3]:mt-4 [&_li]:my-1 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-3 [&_strong]:text-accent [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-5">
              <ReactMarkdown>{data?.diagnostico ?? ""}</ReactMarkdown>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
